import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { AlertCircle } from 'lucide-react';
import { useContext } from 'react';

export interface MessageModalMessage {
  id?: string;
  messageText: string;
  expiresAt: Date;
  isUrgent: boolean;
}

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  messages: MessageModalMessage[];
}

export default function MessageModal({ isOpen, onClose, messages }: MessageModalProps) {
  const { translator } = useContext(LanguageProviderContext);
  const hasAnyUrgent = messages.some((m) => m.isUrgent);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className={`h-5 w-5 ${
              hasAnyUrgent
                ? 'text-red-600 dark:text-red-400'
                : 'text-yellow-600 dark:text-yellow-400'
            }`} />
            {hasAnyUrgent ? translator('Messages.urgentMessage') : translator('Messages.message')}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          {messages.map((message, index) => {
            const key = message.id ?? index;
            const { isUrgent, messageText, expiresAt } = message;
            return (
              <div key={key}>
                <div className={`p-4 rounded-lg ${
                  isUrgent
                    ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800'
                    : 'bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800'
                }`}>
                  <p className={`text-base leading-relaxed ${
                    isUrgent
                      ? 'text-red-900 dark:text-red-100'
                      : 'text-yellow-900 dark:text-yellow-100'
                  }`}>
                    {messageText}
                  </p>
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  <p>{translator('Messages.expiresAt')}: {new Date(expiresAt).toLocaleString()}</p>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
